import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text11 from '@sdk/components/ui/typography/Text11';
import Column from '@sdk/components/ui/spacing/Column';
import { Collapsible } from '@sdk/components/Collapsible/Collapsible';

import SettingsSection from '../../../settings/components/SettingsSection';
import SettingsSwitcherRow from '../../../settings/components/SettingsSwitcherRow';
import { FIELD_WIDTH_100, UNIT_SYSTEMS } from '../../../settings/constants';
import { getNumberFromBoolean } from '../../../settings/helpers/utils';
import { CustomTextField } from '../components/CustomTextField';

import type {
  ClockInGeoRequiredField,
  ClockInMaxDistanceField,
  ImperialUnitsField,
} from './types/TimeclockFields';

type Props = {
  clockInGeoRequiredField: ClockInGeoRequiredField;
  imperialUnitsField: ImperialUnitsField;
  clockInMaxDistanceField: ClockInMaxDistanceField;
};

const Geofencing: React.FC<React.PropsWithChildren<Props>> = ({
  clockInGeoRequiredField,
  imperialUnitsField,
  clockInMaxDistanceField,
}) => {
  return (
    <SettingsSection withSeparator>
      <CollapsibleSection
        isActive={clockInGeoRequiredField.value}
        headerItem={
          <SettingsSwitcherRow
            title={gettext('Geofencing')}
            subtitle={gettext(
              'Enable geofencing to ensure employees are in the right place when they are clocking in and out'
            )}
            value={clockInGeoRequiredField.value}
            onChange={(value): void => {
              clockInGeoRequiredField.onChange(value);
              !value && clockInMaxDistanceField.dirty ? clockInMaxDistanceField.reset() : null;
            }}
          />
        }
        contentItem={
          <Row pTop={10} justifyContent="space-between">
            <Text14 themeColor={ThemeColorType.gray} pRight={20}>
              <TranslateWithComponents
                replaceComponents={{
                  distance: (
                    <Text14 themeColor={ThemeColorType.primary}>
                      {clockInMaxDistanceField.value}
                    </Text14>
                  ),
                  uints: (
                    <Text14 themeColor={ThemeColorType.primary}>
                      {imperialUnitsField.value ? 'feet' : 'meters'}
                    </Text14>
                  ),
                }}>
                {gettext(
                  'Employee can clock in and out within [[distance]] [[uints]] from the location’s address.'
                )}
              </TranslateWithComponents>
            </Text14>
            <Column>
              <Row justifyContent="flex-end">
                <CustomTextField
                  width={FIELD_WIDTH_100}
                  skipErrorMessage
                  value={clockInMaxDistanceField.value || ''}
                  onChange={(event): void =>
                    clockInMaxDistanceField.onChange(Number(event.currentTarget.value) || 0)
                  }
                  invalid={!!clockInMaxDistanceField.error}
                  onBlur={clockInMaxDistanceField.onBlur}
                />
                <Row left={10}>
                  <Picker
                    customWidth={FIELD_WIDTH_100}
                    options={UNIT_SYSTEMS}
                    value={getNumberFromBoolean(imperialUnitsField.value)}
                    onChange={(value: number): void => imperialUnitsField.onChange(Boolean(value))}
                  />
                </Row>
              </Row>
              <Collapsible isActive={!!clockInMaxDistanceField.error}>
                <Collapsible.Content>
                  <Text11 pTop={5} bold themeColor={ThemeColorType.danger}>
                    {clockInMaxDistanceField.error}
                  </Text11>
                </Collapsible.Content>
              </Collapsible>
            </Column>
          </Row>
        }
      />
    </SettingsSection>
  );
};

export default Geofencing;
